/**
 * A best-effort push channel, additive to the poll and visibility triggers
 * (ADR-0005) — nothing here is allowed to be the only way a change is noticed.
 *
 * Not `EventSource`: it cannot carry a custom `Authorization` header, and
 * ADR-0003 forbids putting the bearer token in a URL instead — it would then
 * travel in a proxy's access log and in browser history. `fetch` can set the
 * header, so the stream is read by hand instead.
 */

/** 1s to start, capped at 30s — the ordinary reconnect ramp. */
const RECONNECT_MIN_MS = 1000
const RECONNECT_MAX_MS = 30_000
/**
 * The retry interval after a `503` (the server's connection cap, ADR-0005).
 * Flat and longer than the ramp's ceiling, so every turned-away device does
 * not retry in near-lockstep against a server that is already at capacity.
 */
const RECONNECT_AFTER_CAP_MS = 60_000

export interface LiveConnection {
  close(): void
}

/**
 * Split accumulated SSE text on the blank line that ends every event, keeping
 * whatever is left (a possibly-incomplete final event) for the next chunk.
 */
function takeEvents(buffer: string): { events: string[]; rest: string } {
  const parts = buffer.split('\n\n')
  const rest = parts.pop() ?? ''
  return { events: parts, rest }
}

/** Whether an event carries a `data:` line — a comment or ping otherwise. */
function isDataEvent(event: string): boolean {
  return event.split('\n').some((line) => line.startsWith('data:'))
}

/**
 * Open the push channel and call `onChange` once per record the server
 * broadcasts. Reconnects on any drop until `close()` is called; the caller
 * decides what `onChange` means (this module knows nothing about Sync State).
 */
export function connectLive(
  opts: { url: string; token: string },
  onChange: () => void,
): LiveConnection {
  let closed = false
  let controller: AbortController | null = null
  let attempt = 0
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  // False until the first successful connect. A gap is only possible after this
  // device has *been* connected and then dropped — the very first connect has
  // nothing to catch up on, since the bootstrap's own initial sync (and the poll,
  // and the visibility trigger) already cover the moment before this channel ever
  // opens. Calling `onChange` there too would just be one more redundant pass on
  // every launch.
  let everConnected = false

  async function readStream(response: Response): Promise<void> {
    if (response.body === null) return

    const reader = response.body.getReader()
    // Non-fatal and streaming: a multi-byte character can legitimately split
    // across two chunks, unlike the server's own request-body decoder, which
    // is refusing input rather than parsing a live stream.
    const decoder = new TextDecoder('utf-8')
    let buffer = ''

    while (!closed) {
      const { done, value } = await reader.read()
      if (done) return

      buffer += decoder.decode(value, { stream: true })
      const { events, rest } = takeEvents(buffer)
      buffer = rest

      for (const event of events) {
        if (isDataEvent(event)) onChange()
      }
    }
  }

  function scheduleReconnect(delayMs: number): void {
    if (closed) return
    retryTimer = setTimeout(() => void run(), delayMs)
  }

  /**
   * Ramp one step and reconnect: the exponent is spent on the failure that just
   * happened and only then raised, so the first retry really waits
   * `RECONNECT_MIN_MS` rather than double it. Both failure paths go through here
   * for the same reason — raising the counter first, in two places, is exactly
   * how the floor stopped being a delay the channel ever waits.
   */
  function retryWithBackoff(): void {
    scheduleReconnect(Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** attempt))
    attempt += 1
  }

  async function run(): Promise<void> {
    if (closed) return
    controller = new AbortController()

    try {
      const response = await fetch(opts.url, {
        headers: { authorization: `Bearer ${opts.token}` },
        signal: controller.signal,
      })

      if (response.status === 503) {
        scheduleReconnect(RECONNECT_AFTER_CAP_MS)
        return
      }
      if (!response.ok) {
        retryWithBackoff()
        return
      }

      attempt = 0
      // A gap may have opened while this connection was down; closing it is the
      // caller's job, exactly like a fresh poll — but only on a *re*connect.
      if (everConnected) onChange()
      everConnected = true
      await readStream(response)
    } catch {
      // Aborted by `close()`, offline, TLS, or the connection simply dropped.
      // Indistinguishable here and treated the same either way.
    }

    if (closed) return
    retryWithBackoff()
  }

  void run()

  return {
    close() {
      closed = true
      if (retryTimer !== null) clearTimeout(retryTimer)
      controller?.abort()
    },
  }
}
